import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Siren, Wrench, TrendUp, TrendDown, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

// Sprint 62.2 — Sunday-morning mode: one banner for inactive players in your
// portfolio, projection movers since the build, and a one-click "Fix all".
export default function SundayBanner({ onFixed, source = "smart" }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  // Dismissal is tied to WHAT the alert says: a brand-new scratch brings the
  // banner back, but clearing it (or clearing the lineups) keeps it gone.
  const dismissKey = `wp_sunday_dismissed_${source}`;
  const [dismissedSig, setDismissedSig] = useState(() => {
    try { return window.localStorage.getItem(dismissKey) || ""; } catch { return ""; }
  });

  const seenRef = useRef(new Set());
  // Sprint 68 — Late Swap Alerts: poll every 60s while the app is open; a
  // player ruled out since the last poll fires a toast + browser notification
  // (the backend emails once per new scratch set via Resend).
  const load = () => api.sundayStatus(source).then((st) => {
    setStatus(st);
    const fresh = (st?.newly_out || []).filter((p) => !seenRef.current.has(String(p.dk_id)));
    (st?.inactive_players || []).forEach((p) => seenRef.current.add(String(p.dk_id)));
    if (fresh.length && seenRef.current.size > fresh.length) {
      const who = fresh.map((p) => `${p.name} (${p.status || "OUT"})`).join(", ");
      toast.error(`Late scratch: ${who} — ${st.inactive_lineups.length} lineup${st.inactive_lineups.length === 1 ? "" : "s"} need a swap`, { duration: 15000 });
      try {
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          new Notification("WarPaint · late scratch", { body: `${who} — open Build to Fix all` });
        }
      } catch { /* noop */ }
    }
  }).catch(() => setStatus(null));
  useEffect(() => {
    load();
    const onRefresh = () => load();
    const timer = setInterval(load, 60_000);
    const onCleared = () => {
      seenRef.current.clear();
      setStatus(null);
      try { window.localStorage.removeItem(dismissKey); } catch { /* noop */ }
      setDismissedSig("");
      load();
    };
    window.addEventListener("wp:fill-gap-completed", onRefresh);
    window.addEventListener("wp:pool-refreshed", onRefresh);
    window.addEventListener("wp:lineups-cleared", onCleared);
    try { if (typeof Notification !== "undefined" && Notification.permission === "default") Notification.requestPermission(); } catch { /* noop */ }
    return () => {
      clearInterval(timer);
      window.removeEventListener("wp:fill-gap-completed", onRefresh);
      window.removeEventListener("wp:pool-refreshed", onRefresh);
      window.removeEventListener("wp:lineups-cleared", onCleared);
    };
  }, [source]);

  if (!status || !status.lineups) return null;
  const inactive = status.inactive_lineups || [];
  const movers = status.movers || [];
  if (!inactive.length && !movers.length) return null;
  const sig = [
    status.lineups,
    inactive.length,
    (status.inactive_players || []).map((p) => `${p.dk_id}:${p.status || ""}`).sort().join(","),
  ].join("|");
  if (dismissedSig === sig) return null;

  const dismiss = () => {
    try { window.localStorage.setItem(dismissKey, sig); } catch { /* noop */ }
    setDismissedSig(sig);
  };

  const fixAll = async () => {
    setBusy(true);
    try {
      const r = await api.fixInactive(source);
      toast.success(`Fixed ${r.fixed.length} lineup${r.fixed.length === 1 ? "" : "s"}${r.failed.length ? ` · ${r.failed.length} need a manual fix` : ""}`);
      try { window.dispatchEvent(new CustomEvent("wp:fill-gap-completed")); } catch { /* noop */ }
      await load();
      onFixed?.(r);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Fix all failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="sunday-banner"
      className={`border px-4 py-3 flex items-start gap-3 flex-wrap ${inactive.length ? "border-[#FF3B30]/60 bg-[#FF3B30]/5" : "border-[#FFCC00]/50 bg-[#FFCC00]/5"}`}
    >
      <Siren size={18} weight="fill" className={`mt-0.5 shrink-0 ${inactive.length ? "text-[#FF3B30] animate-pulse" : "text-[#FFCC00]"}`} />
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-400">{inactive.length ? "Late Swap Alert · ruled out since your last build" : "Pre-Lock Check · since your last build"}{source === "raw" ? <span className="ml-2 px-1.5 py-0.5 border border-[#FF8A00]/60 text-[#FF8A00]">Manual lineups</span> : null}</div>
        {inactive.length > 0 && (
          <div className="text-sm text-white" data-testid="sunday-inactive-summary">
            <span className="font-black text-[#FF3B30]">{inactive.length}</span> of {status.lineups} lineups hold an inactive player
            {" — "}
            <span className="text-zinc-300">
              {(status.inactive_players || []).slice(0, 4).map((p) => `${p.name} (${p.status || "OUT"})`).join(" · ")}
              {(status.inactive_players || []).length > 4 ? " · …" : ""}
            </span>
          </div>
        )}
        {inactive.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap" data-testid="sunday-inactive-lineups">
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 mr-1">Lineups</span>
            {inactive.slice(0, 40).map((id) => (
              <a key={id} href="#lineups" data-testid={`sunday-lineup-chip-${id}`}
                 onClick={() => { try { window.dispatchEvent(new CustomEvent("wp:jump-to-lineup", { detail: { lineup_id: id } })); } catch { /* noop */ } }}
                 className="mono text-[10px] px-1.5 py-0.5 border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black transition-colors">
                #{id}
              </a>
            ))}
            {inactive.length > 40 && <span className="mono text-[10px] text-zinc-500">+{inactive.length - 40} more</span>}
          </div>
        )}
        {movers.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="sunday-movers">
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">Movers</span>
            {movers.slice(0, 6).map((m) => (
              <span
                key={m.dk_id}
                title={`${m.name}: built at ${m.built} → now ${m.now} · in ${m.lineups} lineup${m.lineups === 1 ? "" : "s"}`}
                className={`inline-flex items-center gap-1 mono text-[10px] px-1.5 py-0.5 border ${m.delta_pct >= 0 ? "border-[#39FF14]/40 text-[#39FF14]" : "border-[#FF3B30]/40 text-[#FF3B30]"}`}
              >
                {m.delta_pct >= 0 ? <TrendUp size={10} weight="bold" /> : <TrendDown size={10} weight="bold" />}
                {m.name} {m.delta_pct >= 0 ? "+" : ""}{m.delta_pct}%
              </span>
            ))}
          </div>
        )}
      </div>
      {inactive.length > 0 && (
        <button
          type="button"
          onClick={fixAll}
          disabled={busy}
          data-testid="sunday-fix-all"
          className="shrink-0 inline-flex items-center gap-1.5 bg-[#FF3B30] hover:bg-[#d92e24] disabled:opacity-60 text-white font-black uppercase tracking-widest text-[11px] px-4 py-2 transition-colors"
        >
          <Wrench size={12} weight="fill" />
          {busy ? "Fixing…" : `Fix all ${inactive.length}`}
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        data-testid="sunday-dismiss"
        title="Clear this alert. It comes back the moment a new player is ruled out, and clears itself when you clear these lineups."
        className="shrink-0 inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-2 py-2 border border-zinc-700 text-zinc-400 hover:border-white hover:text-white transition-colors"
      >
        <X size={11} weight="bold" /> Clear
      </button>
    </div>
  );
}
